import React from "react";
import * as icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { scrollToSection } from "@/storefront/registry";
import { trackInteraction } from "@/storefront/trackInteraction";

/** Renders `action.icon` (a lucide-react export name, e.g. "Utensils") when it's a real icon —
 * never throws on an unrecognised name, just renders nothing, so a typo in `registry.js` degrades
 * to a label-only pill instead of crashing the page. */
function QuickActionIcon({ name }) {
  const Icon = name && icons[name];
  if (!Icon) return null;
  return <Icon size={15} />;
}

/** The cream booking strip (design floor's "quick action" band): a descriptor line on the left
 * (category · locality) and up to 2 CTAs on the right, primary + secondary styles, each with the
 * handoff's own icon (`design_handoff_business_websites/*.dc.html` QUICK STRIP) — matches the
 * handoff's specific per-capability copy ("See menu" + "Reserve a table") instead of always
 * collapsing to one generic "Book" button (Storefront S1 B3; S5 gap review item 4). `actions` is
 * `quickActionsFor`'s result (registry.js); renders nothing when the business has none. */
export function QuickActions({ actions, descriptor, businessId }) {
  if (!actions || actions.length === 0) return null;
  return (
    <div className="w-full bg-[var(--brand-cream,#f7f3ec)] py-4">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-5 sm:px-8">
        {descriptor && (
          <p className="min-w-0 truncate text-sm text-muted-foreground" data-testid="storefront-quick-descriptor">
            {descriptor}
          </p>
        )}
        <div className="flex shrink-0 items-center gap-2">
          {actions.map((action, i) => {
            const testId = i === 0 ? "storefront-quick-action" : `storefront-quick-action-${i}`;
            const variant = i === 0 ? "default" : "outline";
            const content = (
              <>
                <QuickActionIcon name={action.icon} />
                {action.label}
              </>
            );
            // RF-10: a `href` action (the phone-first Call CTA) is a real link, not a scroll.
            return action.href ? (
              <Button key={action.key} asChild variant={variant} className="gap-2 rounded-full" data-testid={testId}>
                <a href={action.href} onClick={() => businessId && trackInteraction(businessId, "call")}>
                  {content}
                </a>
              </Button>
            ) : (
              <Button
                key={action.key}
                variant={variant}
                className="gap-2 rounded-full"
                data-testid={testId}
                onClick={() => scrollToSection(action.key)}
              >
                {content}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
